/** @jsx jsx */
import { useContext } from "react"
import { jsx, useThemeUI } from "theme-ui"
import PropTypes from "prop-types"

import ModeContext from "../context/mode-context"
import Link from "./link"
import ButtonContent from "./button-content"
import { ab } from "../theme"
import { getButtonTokens, buttonColorSx } from "./button-variants"

const Button = ({
  buttonText,
  link,
  linkTarget,
  download,
  style,
  type,
  onClick,
  language,
  padding,
  align,
  mode,
  variant,
  iconLeft,
  iconRight,
}) => {
  const { mode: globalMode } = useContext(ModeContext)
  const { rawColors: colors } = useThemeUI().theme

  const modeKey = mode ? mode : globalMode
  const modeColor = modeKey ? colors.modes[modeKey].primary : "#000"

  const tokens = getButtonTokens({ variant, businessUnitColor: modeColor })

  const colorSx = buttonColorSx(tokens)
  // theme-ui expands `variant` first, so the variant's `backgroundColor` key
  // sits ahead of the token's `bg` alias and `bg` wins — whatever the spread
  // order. A caller setting `style.backgroundColor` (the search's area
  // filters) got a transparent button with white text (SUP-624).
  if (style.backgroundColor !== undefined) delete colorSx.bg

  const buttonSx = {
    ...colorSx,
    px: 4,
    py: 2,
  }

  const content = (
    <ButtonContent
      text={buttonText}
      iconLeft={iconLeft}
      iconRight={iconRight}
    />
  )

  if (link) {
    return (
      <div
        sx={{
          textAlign: align ? align : "left",
          pr: "35px",
          pl: ab({ _: 3, md: 4 }),
          mb: "10px",
        }}
      >
        <Link
          to={link}
          target={linkTarget}
          download={download}
          language={language}
          sx={{
            p: padding ? 4 : 0,
          }}
        >
          <button
            sx={{
              ...buttonSx,
              ...style,
            }}
          >
            {content}
          </button>
        </Link>
      </div>
    )
  } else {
    return (
      <button
        type={type}
        onClick={onClick}
        sx={{
          ...buttonSx,
          ...style,
        }}
      >
        {content}
      </button>
    )
  }
}

Button.propTypes = {
  buttonText: PropTypes.string.isRequired,
  link: PropTypes.string,
  linkTarget: PropTypes.string,
  download: PropTypes.bool,
  variant: PropTypes.oneOf([
    "ghost",
    "solid",
    "primary",
    "secondary",
    "download",
  ]),
  iconLeft: PropTypes.string,
  iconRight: PropTypes.string,
  style: PropTypes.object,
  type: PropTypes.string,
  onClick: PropTypes.func,
  padding: PropTypes.bool,
  align: PropTypes.string,
  mode: PropTypes.oneOf([
    "",
    "pharma",
    "food",
    "environment",
    "career",
    "construction",
    "medical",
    "lifeScience",
    "agroChemical",
    "sustainabilityServices",
    "none",
  ]),
}

Button.defaultProps = {
  download: false,
  style: {},
  variant: "ghost",
}

export default Button
